import { VisitRiskNtrmGetDto } from "./InspectionNtrmModel";
import { VisitArgument, VisitHeader, VisitMaterial } from "./InspectionVisitModel";
import {
  VisitWorkerGetDto,
  WorkerActGetDto,
  WorkerMajorGetDto,
  WorkerMinorGetDto,
  WorkerTrainingGetDto,
} from "./InspectionWorkerModel";

export interface Inspection {
  visitHeader: VisitHeader;
  visitArguments?: VisitArgument[];
  visitMaterials?: VisitMaterial[];
  visitRiskNtrm?: VisitRiskNtrmGetDto | null;
  visitWorker?: VisitWorkerGetDto | null;
  visitWorkerMajors?: WorkerMajorGetDto[];
  visitWorkerMinors?: WorkerMinorGetDto[];
  visitWorkerActs?: WorkerActGetDto[];
  visitWorkerTraining?: WorkerTrainingGetDto | null;
}

export interface SelectedVisitDetails {
  type: Option;
  object: Option;
  notes: string | null;
}

export interface Nation {
  id_nazione: number;
  deno_nazione: string;
  istatn: string;
  continente: number;
}

export interface Pesticide {
  pkId: number;
  dtInsert: string;
  dtUpdate: string;
  cpaType: string;
  commercialName: string;
  uomActivities: string;
  uomSeedling: string;
  preHarvestInterval: string;
  reEntryInterval: string;
  flActivitiesOn: number;
  flSeedlingOn: number;
  pesticidePrinciples: PesticidePrinciple[];
}

export interface PesticidePrinciple {
  pkId: number;
  dtInsert: string;
  dtUpdate: string;
  pesticideRegistryId: number;
  principle: string;
  principlePercentage: number;
}

export interface Fertilizer {
  pkId: number;
  dtInsert: string;
  dtUpdate: string;
  manufacturerAndCommercialName: string;
  manufacturer: string;
  commercialName: string;
  totalNitrogen: number;
  nitrate: number;
  ammonium: number;
  organic: number;
  ureic: number;
  po25: number;
  k20: number;
  notes: string;
}

export interface TobaccoVariety {
  code: string;
  subCode: string;
  dsDecoding: string;
}

export interface VisitPlotWithGroupDto {
  plotId: number;
  plotCode: string;
  plotDescription: string;
  culture: string;
  city: string;
  area: number;
  lotGroupId: number;
  lotGroupDescription: string;
}

export interface VisitPlotWithVarietyAndGroupDto extends VisitPlotWithGroupDto {
  tobaccoVariety: string;
  validTo: string;
  validFrom: string;
}

export interface LotGroup {
  lotGroupId: number | null;
  lotGroupDescription: string | null;
  tobaccoVariety: string;
}

export interface SeedGetDto {
  agm_group_code: string;
  clone_of: string | null;
  company_name: string | null;
  ds_agm_group: string;
  ds_precocity: string;
  ds_unimis_family: string;
  gddx: string | null;
  grape_bunch_avg_weight: number | null;
  grape_fertility_ratio: number | null;
  has_clones: number;
  interrow_def_distance: number | null;
  is_shared: number;
  is_used: number;
  item_code: string;
  item_id: number;
  item_name: string;
  item_note: string | null;
  last_update: number;
  main_variety: string | null;
  manufacturer_code: string | null;
  manufacturer_id: string | null;
  physical_quantity: string;
  plants_def_distance: number | null;
  precocity_s_code: string;
  seed_avg_weight: number | null;
  seed_variety_id: number;
  unimis_family: string;
}

export interface ShownModules {
  materialsDocumentsArguments: boolean;
  worker: boolean;
  riskNtrm: boolean;
  seedling: boolean;
  plot: boolean;
  monitoringYield: boolean;
  alpMonitoringForm: boolean;
  garbageBestPractice: boolean;
  cpaStorageSecurity: boolean;
  alpWashAccommodation: boolean;
  survey: boolean;
  fieldActivities: boolean;
  dacBuDryers: boolean;
  fcvDryers: boolean;
  damage: boolean;
  fertilizationPlan: boolean;
  visitView: boolean;
  visitEdit: boolean;
}

export interface UserPermissions {
  alpWashAccommodationCanEditWithAccident: boolean;
  accidentCanEditAndDeleteAccident: boolean;
  accidentCanEditAndDeleteFollowUp: boolean;
}

export interface Option {
  message?: string | null;
  value: string;
  text: string;
}

export interface OptionQta extends Option {
  qta: number | null;
}

export interface OptionParent extends Option {
  parent: string;
}

export interface OptionParentCount extends OptionParent {
  count: number;
}
export interface OptionParentValid extends OptionParentCount {
  isValid: boolean;
}

export interface OptionParentArea extends OptionParent {
  area: number;
}

export interface OptionParentMetaData extends OptionParent {
  metadata: {
    metadataCode: string;
    metadataValue: string;
  }[];
}
export interface OptionParentMetaDataValid extends OptionParentValid {
  metadata: {
    metadataCode: string;
    metadataValue: string;
  }[];
}

export enum FormMode {
  NEW = 0,
  EDIT = 1,
  VIEW = 2,
  LIST = 3,
}

export enum SectionMode {
  LIST = 0,
  NEW = 1,
  EDIT = 2,
  VIEW = 3,
}

export enum SectionModeTotal {
  LIST = 0,
  NEW = 1,
  EDIT = 2,
  VIEW = 3,
  TOTAL = 4,
}

export enum SectionModeAttachments {
  LIST = 0,
  NEW = 1,
  EDIT = 2,
  VIEW = 3,
  ATTACHMENTS = 4,
}

export enum SectionModeAttachmentsWithSelection {
  LIST = 0,
  NEW = 1,
  EDIT = 2,
  VIEW = 3,
  SELECTION = 4,
  ATTACHMENTS = 5,
}

export interface ViewModules {
  MATERIALS_DOCUMENTS_ARGUMENTS: boolean;
  WORKER: boolean;
  RISK_NTRM: boolean;
}

export const METADATA_CODES = Object.freeze({
  SITUATION_TYPE: "SITUATION_TYPE",
  FOLLOW_UP_EXPIRATION: "FOLLOW_UP_EXPIRATION",
  WEEKS_TO_ADD: "WEEKS_TO_ADD",
  ANSWER_INPUT_TYPE: "ANSWER_INPUT_TYPE",
  REASON_INPUT_TYPE: "REASON_INPUT_TYPE",
  ACT_PLAN_INPUT_TYPE: "ACT_PLAN_INPUT_TYPE",
  COMMENT: "COMMENT",
  ACTION: "ACTION",
});

export const METADATA_VALUES = Object.freeze({
  FACOLTATIVO: "Facoltativo",
  OBBLIGATORIO: "Obbligatorio",
  APRI_INCIDENTE: "Apri incidente",
  NESSUNA: "Nessuna",
});

export const INPUT_TYPES = Object.freeze({
  DECOCODE: "DECOCODE",
  NUMBER: "NUMBER",
  TEXT: "TEXT",
});
